import {
  type NameProps,
  pickNameProps,
} from '@wandb/weave/common/util/reactUtils';
import {makePropsAreEqual} from '@wandb/weave/common/util/shouldUpdate';
import {clsx} from 'clsx';
import _ from 'lodash';
import React, {
  type FC,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {Link} from 'react-router-dom';
import {Popup} from 'semantic-ui-react';

import {textWidth} from '../../util/text';

interface EditableLabelProps {
  onSave?: (text: string) => void; // Debounced. Will be called whenever the text changes.
  readOnly?: boolean;
  serverText: string | undefined; // the value that's shown when not editing
  title?: string;
  linkTo?: string; // url to link displayText to
  placeholder?: string;
  onClick?: (e: React.SyntheticEvent) => void; // maps to input onClick, but only when in non-edit mode.
  onBlur?: (text: string) => void; // maps directly to native input onBlur
  editing?: boolean; // if provided, tells the component whether or not to be in editing mode
  showPopup?: boolean; // if true, this will show the text in a popup on hover
  extraMarginRight?: string; // extra right margin for the text, e.g. "10px"
  dataTest?: string;
}

/**
 * Create a label that is editable on click and synced with the server
 *  The parent can also control the edit state by passing the editing property.
 *
 * @deprecated prefer EditableField
 */
const EditableLabelComp: FC<EditableLabelProps & NameProps> = (
  props: EditableLabelProps & NameProps
) => {
  const {
    onSave,
    readOnly,
    serverText,
    title,
    linkTo,
    placeholder,
    onClick,
    onBlur,
    editing: propsEditing,
    showPopup,
    className,
    extraMarginRight = '0px',
    dataTest,
  } = props;
  const [editing, setEditing] = useState(propsEditing ?? false);
  const [text, setText] = useState<string | undefined>(serverText);

  const textInput = useRef<HTMLInputElement | null>(null);

  const debouncedSave = useMemo(
    () =>
      !readOnly && onSave != null
        ? _.debounce((notes: string) => {
            onSave(notes);
          }, 500)
        : null,
    [readOnly, onSave]
  );

  const handleFocus = useCallback(() => {
    setEditing(propsEditing ?? true);
  }, [propsEditing]);

  const handleBlur = useCallback(() => {
    if (onBlur) {
      onBlur(text || '');
    }
    setEditing(false);
  }, [onBlur, text]);

  const handleKeyPress = useCallback(
    (e: any) => {
      if (e.key === 'Enter') {
        handleBlur();
      }
    },
    [handleBlur]
  );

  const handleChange = useCallback(() => {
    if (textInput.current != null) {
      const val = textInput.current.value;
      setText(val);
      if (val !== serverText && debouncedSave != null) {
        debouncedSave(val);
      }
    }
  }, [serverText, debouncedSave]);

  useEffect(() => {
    if (serverText != null && text == null && text !== serverText) {
      setText(serverText);
    }
  }, [serverText, text]);

  const editingLabel = readOnly ? false : propsEditing || editing;

  const fullClassName = clsx('editable-label', className, {
    placeholder: !text || text === '',
    disabled: readOnly,
  });

  const displayText = !text || text === '' ? placeholder : text;
  const inputWidth = 20 + textWidth(displayText || '');

  const displaySpan = (
    <span className={fullClassName} onClick={onClick || handleFocus}>
      {displayText}
    </span>
  );
  return (
    <span
      style={{marginRight: extraMarginRight}}
      {...Object.assign(pickNameProps(props), {className: fullClassName})}
      title={title}
      data-test={dataTest}>
      {editingLabel ? (
        <input
          style={{width: inputWidth, backgroundColor: 'white', color: 'black'}}
          type="text"
          ref={textInput}
          value={text}
          onChange={handleChange}
          onBlur={handleBlur}
          onKeyPress={handleKeyPress}
          placeholder={placeholder}
          onClick={e => e.stopPropagation()}
          autoFocus
        />
      ) : linkTo ? (
        <Link to={linkTo}>{displayText}</Link>
      ) : showPopup && displayText !== placeholder ? (
        <Popup
          basic
          flowing
          content={displayText}
          trigger={displaySpan}
          popperModifiers={{
            preventOverflow: {
              boundariesElement: 'viewport',
            },
          }}
        />
      ) : (
        displaySpan
      )}
    </span>
  );
};

export const EditableLabel = memo(
  EditableLabelComp,
  makePropsAreEqual({
    name: 'EditableLabel',
    deep: [],
    ignore: [],
    ignoreFunctions: true,
    debug: false,
    verbose: true,
  })
);
